import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";

import { useDarkMode } from "../../../context/ThemeContext";
import { getThemeClasses } from "../../../utils/ThemeClasses";
import SgTable from "../../sg/components/SgTable";
import type { SgColumn } from "../../sg/components/SgTable";
import {
  fetchArchiveJobs,
  deleteArchiveBackup,
  formatArchiveDate,
} from "../../../features/device-archive/jobs";
import type { ArchiveJob } from "../../../features/device-archive/jobs";
import { StatusBadge, ReasonBadge } from "../components/badges";
import { confirmStrongDelete } from "../functions";

function sumTotals(job: ArchiveJob): number {
  const t = job.totals || { positions: 0, events: 0, commands: 0, geofences: 0 };
  return (t.positions || 0) + (t.events || 0) + (t.commands || 0) + (t.geofences || 0);
}

export default function RespaldosList() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation("respaldos");
  const { isDarkMode } = useDarkMode();
  const themeClasses = getThemeClasses(isDarkMode);
  const queryClient = useQueryClient();

  const { data, isPending, isError, error } = useQuery({
    queryKey: ["archive-jobs"],
    queryFn: () => fetchArchiveJobs(),
  });

  const rows = data ?? [];

  async function handleDelete(job: ArchiveJob) {
    const ok = await confirmStrongDelete({
      title: t("delete.title"),
      text: t("delete.textJob"),
      prompt: t("delete.prompt", { word: t("delete.word") }),
      word: t("delete.word"),
      mismatch: t("delete.mismatch"),
      confirm: t("delete.confirm"),
      cancel: t("delete.cancel"),
    });
    if (!ok) return;
    Swal.fire({
      title: t("delete.deleting"),
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
      didOpen: () => Swal.showLoading(),
    });
    try {
      await deleteArchiveBackup(job.id);
      await queryClient.invalidateQueries({ queryKey: ["archive-jobs"] });
      await queryClient.invalidateQueries({ queryKey: ["archive-job", job.id] });
      Swal.fire({
        icon: "success",
        title: t("delete.done"),
        timer: 1800,
        showConfirmButton: false,
      });
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: t("delete.error"),
        text: (err as Error)?.message,
        confirmButtonColor: "#dc2626",
      });
    }
  }

  const columns: SgColumn[] = [
    {
      header: t("jobs.cols.company"),
      key: "companyName",
      render: (j: ArchiveJob) => {
        const meta = [j.businessName, j.rfc, j.email].filter(Boolean).join(" · ");
        return (
          <div className="flex flex-col">
            <span className="font-medium">
              {j.companyName || j.companyId || "—"}
            </span>
            {meta && (
              <span className={`text-xs ${themeClasses.textSecondary}`}>{meta}</span>
            )}
          </div>
        );
      },
    },
    {
      header: t("jobs.cols.reason"),
      key: "reason",
      render: (j: ArchiveJob) => (
        <ReasonBadge
          reason={j.reason}
          label={t(`reason.${j.reason}`)}
          isDarkMode={isDarkMode}
        />
      ),
    },
    {
      header: t("jobs.cols.status"),
      key: "status",
      render: (j: ArchiveJob) => (
        <StatusBadge
          status={j.status}
          label={t(`status.${j.status}`)}
          isDarkMode={isDarkMode}
        />
      ),
    },
    {
      header: t("jobs.cols.devices"),
      key: "devices",
      align: "center",
      render: (j: ArchiveJob) => (
        <span>
          {j.doneCount}/{j.deviceCount}
          {j.failedCount > 0 && (
            <span className="text-red-500"> ({j.failedCount})</span>
          )}
        </span>
      ),
    },
    {
      header: t("jobs.cols.totals"),
      key: "totals",
      align: "center",
      render: (j: ArchiveJob) => (
        <span>{sumTotals(j).toLocaleString(i18n.language)}</span>
      ),
    },
    {
      header: t("jobs.cols.created"),
      key: "createdAt",
      render: (j: ArchiveJob) =>
        formatArchiveDate(j.createdAt, i18n.language),
    },
    {
      header: t("jobs.cols.actions"),
      key: "actions",
      align: "right",
      render: (j: ArchiveJob) => (
        <div className="flex items-center justify-end gap-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/respaldos/${j.id}`);
            }}
            className="text-sm px-3 py-1.5 rounded-lg font-medium bg-primary-500 hover:bg-primary-400 text-white transition-colors whitespace-nowrap"
          >
            {t("jobs.openDetail")}
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleDelete(j);
            }}
            disabled={j.status === "deleted"}
            className={`text-sm px-2.5 py-1.5 rounded-lg font-medium border transition-colors ${
              isDarkMode
                ? "border-dark-border text-red-400 hover:bg-white/5"
                : "border-gray-200 text-red-600 hover:bg-gray-50"
            } disabled:opacity-40 disabled:cursor-not-allowed`}
          >
            <i className="ri-delete-bin-line"></i>
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="h-full min-h-0 w-full flex flex-col">
      <div
        className={`px-6 pt-6 pb-5 border-b ${
          isDarkMode ? "border-dark-border" : "border-gray-200"
        }`}
      >
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className={`text-2xl font-bold ${themeClasses.text}`}>
              {t("jobs.title")}
            </h1>
            <p className={`text-sm mt-1 ${themeClasses.textSecondary}`}>
              {t("jobs.subtitle")}
            </p>
          </div>
          <span className={`text-sm ${themeClasses.textSecondary}`}>
            {t("jobs.count", { n: rows.length.toLocaleString(i18n.language) })}
          </span>
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto px-6 py-6">
        {isError ? (
          <p className="text-red-500 text-sm">
            {(error as Error)?.message || t("jobs.loadError")}
          </p>
        ) : (
          <SgTable
            columns={columns}
            rows={rows}
            isDarkMode={isDarkMode}
            loading={isPending}
            emptyText={t("jobs.empty")}
            rowKey={(j: ArchiveJob) => j.id}
            onRowClick={(j: ArchiveJob) => navigate(`/respaldos/${j.id}`)}
          />
        )}
      </div>
    </div>
  );
}
